<template>
  <div class="email-restrictions">
    <common-checkbox v-model="enabled">
      Add email restrictions
    </common-checkbox>

    <div v-if="emailRestrictions.enabled" class="email-restrictions__card">
      <div class="email-restrictions__header">
        <div>
          <div class="email-restrictions__title">Email restrictions</div>
          <div class="email-restrictions__subtitle">
            Each email address can redeem one gift. Choose how you limit which addresses are accepted.
          </div>
        </div>
      </div>

      <v-divider class="my-4" />

      <div class="email-restrictions__section">
        <common-radio v-model="mode" :inputValue="EMAIL_RESTRICTION_MODE.BLOCK_FREE_DOMAINS">
          <div>
            <div class="email-restrictions__option-title">Block free email domains</div>
            <div class="email-restrictions__option-subtitle">Recipients must redeem with a work email address.</div>
          </div>
        </common-radio>

        <div v-if="mode === EMAIL_RESTRICTION_MODE.BLOCK_FREE_DOMAINS" class="email-restrictions__box">
          <div class="email-restrictions__box-header">
            <span class="email-restrictions__box-label">Blocked providers</span>
            <span class="email-restrictions__box-actions">
              <span @click="selectAllFreeDomains()">Select all</span>
              <span class="email-restrictions__box-actions-clear" @click="clearFreeDomains()">Clear</span>
            </span>
          </div>

          <v-row no-gutters class="email-restrictions__checkbox-grid">
            <v-col
              v-for="freeDomain in freeEmailDomains"
              :key="freeDomain.domain"
              cols="4"
            >
              <common-checkbox
                :value="emailRestrictions.blockedDomains.includes(freeDomain.domain)"
                @input="(checked) => toggleBlockedDomain(freeDomain.domain, checked)"
              >
                {{ freeDomain.label }}
              </common-checkbox>
            </v-col>
            <v-col cols="4">
              <common-checkbox v-model="includeOtherFreeProviders">
                Other free providers
                <v-menu
                  open-on-hover
                  :close-on-content-click="false"
                  top
                  offset-y
                  z-index="8"
                  max-width="260"
                  content-class="email-restrictions__hover-menu"
                >
                  <template #activator="{ on, attrs }">
                    <div class="d-flex" v-on="on" v-bind="attrs">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#95979D" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <path d="M12 17h.01" />
                      </svg>
                    </div>
                  </template>
                  <span
                    class="email-restrictions__tooltip-link"
                    @click="showFreeEmailProvidersModal = true"
                  >View full list of free email providers here</span>.
                </v-menu>
              </common-checkbox>
            </v-col>
          </v-row>

          <div v-if="blockedDomainsCount === 0" class="email-restrictions__error">
            Select at least one provider to block.
          </div>
        </div>

        <the-free-email-providers-modal v-model="showFreeEmailProvidersModal" />

        <div v-if="mode === EMAIL_RESTRICTION_MODE.BLOCK_FREE_DOMAINS" class="email-restrictions__info">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="flex: 0 0 18px;">
            <path d="M12 3.5l7.5 3v5.2c0 4.2-3 7.1-7.5 8.8-4.5-1.7-7.5-4.6-7.5-8.8V6.5l7.5-3z" stroke="#62646a" stroke-width="1.4" stroke-linejoin="round" />
          </svg>
          <template v-if="blockedDomainsCount > 0">
            Anyone using one of these {{ blockedDomainsCount }} free provider{{ blockedDomainsCount === 1 ? '' : 's' }} will be asked for a different email address. One gift per address.
          </template>
          <template v-else>
            No restriction applied yet - any email address can redeem a gift.
          </template>
        </div>
      </div>

      <v-divider class="my-4" />

      <div class="email-restrictions__section">
        <common-radio v-model="mode" :inputValue="EMAIL_RESTRICTION_MODE.ALLOW_SPECIFIC_DOMAINS">
          <div>
            <div class="email-restrictions__option-title">Allow only specific email domains</div>
            <div class="email-restrictions__option-subtitle">Only addresses ending with the domains you list can redeem.</div>
          </div>
        </common-radio>

        <div v-if="mode === EMAIL_RESTRICTION_MODE.ALLOW_SPECIFIC_DOMAINS" class="email-restrictions__box">
          <div class="email-restrictions__box-label">Allowed domains</div>

          <div class="email-restrictions__add-domain">
            <common-input
              v-model="domainInput"
              placeholder="Enter a domain, for example acme.com"
              height="40"
              style="max-width: 250px"
              @keyup.enter="addDomain()"
            />
            <common-button outlined height="40" @click="addDomain()">
              Add domain
            </common-button>
          </div>

          <div v-if="domainError" class="email-restrictions__error">
            {{ domainError }}
          </div>

          <div v-if="emailRestrictions.allowedDomains.length" class="email-restrictions__chips">
            <span
              v-for="allowedDomain in emailRestrictions.allowedDomains"
              :key="allowedDomain"
              class="email-restrictions__chip"
            >
              {{ allowedDomain }}
              <span @click="removeDomain(allowedDomain)">&times;</span>
            </span>
          </div>
        </div>

        <div v-if="mode === EMAIL_RESTRICTION_MODE.ALLOW_SPECIFIC_DOMAINS" class="email-restrictions__info">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="flex: 0 0 18px;">
            <path d="M12 3.5l7.5 3v5.2c0 4.2-3 7.1-7.5 8.8-4.5-1.7-7.5-4.6-7.5-8.8V6.5l7.5-3z" stroke="#62646a" stroke-width="1.4" stroke-linejoin="round" />
          </svg>
          <template v-if="emailRestrictions.allowedDomains.length">
            Only addresses ending with {{ allowedDomainsText }} can redeem. One gift per address.
          </template>
          <template v-else>
            No restriction applied yet - any email address can redeem a gift.
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FREE_EMAIL_DOMAINS } from './freeEmailDomains'
import { EMAIL_RESTRICTION_MODE } from './emailRestrictionMode'
import TheFreeEmailProvidersModal from './TheFreeEmailProvidersModal.vue'

const DOMAIN_REGEX = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i

export default {
  name: 'TheRecipientsAreaEmailRestrictions',
  components: {
    TheFreeEmailProvidersModal,
  },
  props: {
    emailRestrictions: {
      type: Object,
      required: false,
      default: () => ({
        enabled: false,
        mode: EMAIL_RESTRICTION_MODE.BLOCK_FREE_DOMAINS,
        blockedDomains: [],
        includeOtherFreeProviders: false,
        allowedDomains: [],
      })
    }
  },
  data: () => ({
    EMAIL_RESTRICTION_MODE,
    freeEmailDomains: FREE_EMAIL_DOMAINS,
    domainInput: '',
    domainError: null,
    showFreeEmailProvidersModal: false,
  }),
  computed: {
    enabled: {
      get () { return this.emailRestrictions.enabled },
      set (val) { val ? this.enable() : this.disable() }
    },
    mode: {
      get () { return this.emailRestrictions.mode },
      set (val) { this.updateRestrictions({ mode: val }) }
    },
    includeOtherFreeProviders: {
      get () { return this.emailRestrictions.includeOtherFreeProviders },
      set (val) { this.updateRestrictions({ includeOtherFreeProviders: val }) }
    },
    blockedDomainsCount () {
      const { blockedDomains, includeOtherFreeProviders } = this.emailRestrictions
      return blockedDomains.length + (includeOtherFreeProviders ? 1 : 0)
    },
    allowedDomainsText () {
      return this.emailRestrictions.allowedDomains.map((domain) => `@${domain}`).join(', ')
    }
  },
  methods: {
    updateRestrictions (patch) {
      this.$emit('update:emailRestrictions', { ...this.emailRestrictions, ...patch })
    },
    enable () {
      this.updateRestrictions({ enabled: true })
    },
    disable () {
      this.domainInput = ''
      this.domainError = null
      this.updateRestrictions({
        enabled: false,
        mode: EMAIL_RESTRICTION_MODE.BLOCK_FREE_DOMAINS,
        blockedDomains: [],
        includeOtherFreeProviders: false,
        allowedDomains: [],
      })
    },
    toggleBlockedDomain (domain, checked) {
      const current = this.emailRestrictions.blockedDomains
      const next = checked
        ? [...new Set([...current, domain])]
        : current.filter((blockedDomain) => blockedDomain !== domain)

      this.updateRestrictions({ blockedDomains: next })
    },
    selectAllFreeDomains () {
      this.updateRestrictions({
        blockedDomains: this.freeEmailDomains.map((freeDomain) => freeDomain.domain),
        includeOtherFreeProviders: true,
      })
    },
    clearFreeDomains () {
      this.updateRestrictions({ blockedDomains: [], includeOtherFreeProviders: false })
    },
    addDomain () {
      const domain = this.domainInput.trim().toLowerCase()

      if (!domain) {
        this.domainError = this.emailRestrictions.allowedDomains.length
          ? 'Enter a domain, for example acme.com'
          : 'Add at least one domain to apply this restriction.'
        return
      }

      if (!DOMAIN_REGEX.test(domain)) {
        this.domainError = 'That is not a valid domain. Try acme.com'
        return
      }

      if (this.emailRestrictions.allowedDomains.includes(domain)) {
        this.domainError = 'That domain has already been added.'
        return
      }

      this.updateRestrictions({ allowedDomains: [...this.emailRestrictions.allowedDomains, domain] })
      this.domainInput = ''
      this.domainError = null
    },
    removeDomain (domain) {
      this.updateRestrictions({
        allowedDomains: this.emailRestrictions.allowedDomains.filter((allowedDomain) => allowedDomain !== domain)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.email-restrictions {
  &__card {
    border: 1px solid #DEDEDE;
    padding: 20px;
    margin-top: 14px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__title {
    font-family: 'Lato-Bold', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }

  &__subtitle, &__option-subtitle {
    font-family: 'Lato-Italic', sans-serif;
    font-style: italic;
    font-size: 14px;
    color: #95979D;
  }

  &__option-title {
    font-family: 'Lato-Bold', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #000;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__box {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 18px;
    margin-left: 29px;
    background: #EDFCFB;
  }

  &__box-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__box-label {
    font-family: 'Lato-Bold', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: #000;
  }

  &__box-actions {
    display: flex;
    gap: 12px;

    > span {
      font-family: 'Lato-Regular', sans-serif;
      font-size: 14px;
      color: #219358;
      cursor: pointer;

      &.email-restrictions__box-actions-clear {
        color: #62646A;
      }
    }
  }

  &__tooltip-link {
    color: #219358;
    cursor: pointer;
  }

  &__checkbox-grid {
    row-gap: 12px;

    ::v-deep .common-checkbox__content {
      gap: 9px;
    }
  }

  &__add-domain {
    display: flex;
    gap: 12px;
    align-items: flex-start;

    ::v-deep .v-btn {
      font-size: 15px;
    }
  }

  &__error {
    font-family: 'Lato-Italic', sans-serif;
    font-style: italic;
    font-size: 13px;
    color: #DD080E;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: #fff;
    border: 1px solid #DEDEDE;
    border-radius: 20px;
    padding: 4px 12px;
    font-family: 'Lato-Regular', sans-serif;
    font-size: 14px;
    color: #000;

    > span {
      cursor: pointer;
      color: #95979D;
    }
  }

  &__info {
    display: flex;
    gap: 12px;
    background-color: #F5F5F5;
    padding: 12px 16px;
    font-family: 'Lato-Regular', sans-serif;
    font-size: 14px;
    color: #62646A;
  }
}
</style>

<style lang="scss">
// Unscoped: v-menu content is detached from this component's DOM subtree,
// so scoped attribute selectors don't reliably reach it (same reason
// common/Tooltip.vue's content styling below is unscoped too).
.email-restrictions__hover-menu {
  background: #fff !important;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.15) !important;
  border-radius: 4px;
  padding: 16px;
  font-family: 'Lato-Regular', sans-serif;
  font-size: 13px;
  line-height: 16px;
  color: #212121;
}
</style>
